import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import ArticleCard from "@/components/ArticleCard";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Home() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      // First try to seed if empty
      await axios.post(`${API}/seed`);
      const response = await axios.get(`${API}/articles`);
      setArticles(response.data);
    } catch (error) {
      console.error("Error fetching articles:", error);
    } finally {
      setLoading(false);
    }
  };

  const featuredArticle = articles[0];
  const latestArticles = articles.slice(1, 7);

  return (
    <div data-testid="home-page">
      <SEO />
      
      {/* Hero Section */}
      <section className="relative" data-testid="hero-section">
        {/* Desktop Hero */}
        <div className="hidden md:block min-h-[80vh]">
          <div className="absolute inset-0 z-0">
            {featuredArticle && (
              <img
                src={featuredArticle.image_url}
                alt={featuredArticle.title}
                className="w-full h-full object-cover"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-r from-white via-white/90 to-transparent" />
          </div>
          
          <div className="container relative z-10 py-24 lg:py-32 min-h-[80vh] flex items-center">
            <div className="max-w-2xl">
              {featuredArticle ? (
                <>
                  <span className="font-accent text-xs font-bold uppercase tracking-widest text-[#FF66C4] mb-4 block animate-fade-in">
                    {featuredArticle.category}
                  </span>
                  <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold leading-none mb-6 animate-fade-in stagger-1">
                    {featuredArticle.title}
                  </h1>
                  <p className="text-lg md:text-xl text-zinc-600 mb-8 leading-relaxed animate-fade-in stagger-2">
                    {featuredArticle.excerpt}
                  </p>
                  <Link
                    to={`/articles/${featuredArticle.slug}`}
                    className="inline-flex items-center gap-2 bg-[#FF66C4] text-white font-bold uppercase tracking-wider px-8 py-4 hover:bg-[#e55ab0] transition-colors animate-fade-in stagger-3"
                    data-testid="hero-read-more-btn"
                  >
                    Read Story
                    <ArrowRight size={18} />
                  </Link>
                </>
              ) : loading ? (
                <div className="space-y-4">
                  <div className="h-4 w-24 skeleton" />
                  <div className="h-16 w-full skeleton" />
                  <div className="h-6 w-3/4 skeleton" />
                </div>
              ) : (
                <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold">
                  Welcome to Filmcable
                </h1>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Hero - Vogue Style */}
        <div className="md:hidden">
          {featuredArticle ? (
            <Link to={`/articles/${featuredArticle.slug}`} className="block" data-testid="hero-mobile-link">
              {/* Full-width image */}
              <div className="relative aspect-[4/5]">
                <img
                  src={featuredArticle.image_url}
                  alt={featuredArticle.title}
                  className="w-full h-full object-cover"
                />
                {/* Gradient overlay at bottom */}
                <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                
                {/* Text content overlaid at bottom */}
                <div className="absolute inset-x-0 bottom-0 p-6 text-center">
                  <span className="font-accent text-xs font-bold uppercase tracking-[0.2em] text-[#FF66C4] mb-3 block">
                    {featuredArticle.category}
                  </span>
                  <h1 className="font-serif text-2xl font-bold leading-tight text-white mb-3">
                    {featuredArticle.title}
                  </h1>
                  <p className="font-accent text-xs uppercase tracking-wider text-white/80 mb-1">
                    By {featuredArticle.author}
                  </p>
                  <p className="text-xs text-white/60">
                    {new Date(featuredArticle.created_at).toLocaleDateString("en-US", {
                      day: "numeric",
                      month: "long",
                      year: "numeric"
                    })}
                  </p>
                </div>
              </div>
            </Link>
          ) : loading ? (
            <div className="aspect-[4/5] skeleton" />
          ) : null}
        </div>
      </section>

      {/* Accent Divider */}
      <div className="h-1 bg-[#FF66C4]" />

      {/* Latest News Section */}
      <section className="py-12 md:py-28" data-testid="latest-news-section">
        <div className="container">
          {/* Header with divider */}
          <div className="mb-8 md:mb-10">
            <h2 className="font-serif text-2xl md:text-3xl font-bold mb-4">READ NOW</h2>
            <div className="h-[1px] bg-zinc-900" />
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="space-y-4">
                  <div className="aspect-[3/4] skeleton" />
                  <div className="h-4 w-20 skeleton" />
                  <div className="h-8 w-full skeleton" />
                  <div className="h-4 w-3/4 skeleton" />
                </div>
              ))}
            </div>
          ) : (
            <>
              {/* Desktop: 4-column grid */}
              <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {latestArticles.slice(0, 4).map((article) => (
                  <ArticleCard
                    key={article.id}
                    article={article}
                    featured={false}
                  />
                ))}
              </div>

              {/* Mobile: Horizontal list layout */}
              <div className="md:hidden space-y-6">
                {latestArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/articles/${article.slug}`}
                    className="flex gap-4 group"
                    data-testid={`mobile-article-${article.slug}`}
                  >
                    {/* Thumbnail */}
                    <div className="w-28 h-28 flex-shrink-0 overflow-hidden bg-zinc-100">
                      <img
                        src={article.image_url}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <span className="font-accent text-xs font-bold uppercase tracking-widest text-[#FF66C4] mb-1 block">
                        {article.category}
                      </span>
                      <h3 className="font-serif text-base font-bold leading-tight mb-2 line-clamp-2 group-hover:text-[#FF66C4] transition-colors">
                        {article.title}
                      </h3>
                      <p className="font-accent text-xs uppercase tracking-wider text-zinc-500">
                        By {article.author}
                      </p>
                      <p className="text-xs text-zinc-400 mt-1">
                        {new Date(article.created_at).toLocaleDateString("en-US", {
                          day: "numeric",
                          month: "long",
                          year: "numeric"
                        })}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}

          <div className="mt-10 text-center">
            <Link
              to="/articles"
              className="inline-flex items-center gap-2 border-2 border-zinc-900 px-6 py-3 font-bold uppercase tracking-wider text-sm hover:bg-zinc-900 hover:text-white transition-colors"
              data-testid="view-all-articles-btn"
            >
              View All Articles
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 bg-zinc-50" data-testid="categories-section">
        <div className="container">
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4">Explore Coverage</h2>
          <div className="accent-line mb-12" />
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { name: "Film", description: "Coverage of new releases and cinema highlights" },
              { name: "Series", description: "Television and streaming series coverage" },
              { name: "Industry", description: "Inside the business of cinema worldwide" },
              { name: "Awards", description: "Oscars, BAFTAs, Golden Globes and more" },
              { name: "Festivals", description: "Cannes, Venice, Berlin, and beyond" },
              { name: "Reviews", description: "Critical analysis of films and series" },
              { name: "Suggestions", description: "Curated recommendations for what to watch" },
              { name: "Stories", description: "Behind-the-scenes and exclusive narratives" }
            ].map((category) => (
              <Link
                key={category.name}
                to={`/articles?category=${category.name}`}
                className="group p-8 border border-zinc-200 bg-white hover:border-[#FF66C4] transition-colors"
                data-testid={`category-${category.name.toLowerCase()}-link`}
              >
                <h3 className="font-serif text-2xl font-bold mb-2 group-hover:text-[#FF66C4] transition-colors">
                  {category.name}
                </h3>
                <p className="text-zinc-600">{category.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 border-t border-zinc-200" data-testid="cta-section">
        <div className="container text-center">
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            The Voice of <span className="italic text-[#FF66C4]">Cinema</span>
          </h2>
          <p className="text-lg md:text-xl text-zinc-600 max-w-2xl mx-auto mb-10">
            Filmcable delivers authoritative coverage of the global film industry, 
            from major festivals to emerging voices shaping the future of cinema.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/about"
              className="inline-flex items-center justify-center gap-2 bg-[#FF66C4] text-white font-bold uppercase tracking-wider px-8 py-4 hover:bg-[#e55ab0] transition-colors"
              data-testid="cta-about-btn"
            >
              About Filmcable
            </Link>
            <Link
              to="/press"
              className="inline-flex items-center justify-center gap-2 border-2 border-zinc-900 px-8 py-4 font-bold uppercase tracking-wider hover:bg-zinc-900 hover:text-white transition-colors"
              data-testid="cta-press-btn"
            >
              Press Inquiries
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
